import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Send, ArrowLeft, Eye, Image as ImageIcon, Type, AlignLeft, Square, Move, Copy, ChevronUp, ChevronDown, GripVertical, ChevronRight, Upload, X, List, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import RichTextEditor from './RichTextEditor';

const BRAND_COLORS = [
  { name: 'White', value: '#ffffff' },
  { name: 'Light Gray', value: '#f3f4f6' },
  { name: 'Teal Primary', value: '#00b48f' },
  { name: 'Dark Green', value: '#00461a' },
  { name: 'Light Teal', value: '#e6f7f4' },
  { name: 'Light Green', value: '#f0f7f4' },
];

const NewsletterBuilder = () => {
  const navigate = useNavigate();
  const [newsletters, setNewsletters] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [currentNewsletter, setCurrentNewsletter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState('drafts'); // 'drafts' | 'published'
  const [collapsedSections, setCollapsedSections] = useState(new Set());
  
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    sections: [],
    target_audience: {
      type: 'whole_company',
      departments: [],
      employee_ids: []
    }
  });

  const toggleSectionCollapse = (sectionIndex) => {
    const newCollapsed = new Set(collapsedSections);
    if (newCollapsed.has(sectionIndex)) {
      newCollapsed.delete(sectionIndex);
    } else {
      newCollapsed.add(sectionIndex);
    }
    setCollapsedSections(newCollapsed);
  };

  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    fetchNewsletters();
    fetchDepartments();
    fetchEmployees();
  }, []);

  const fetchNewsletters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletters/manager/all`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        cache: 'no-store'
      });
      const data = await response.json();
      setNewsletters(data);
    } catch (error) {
      console.error('Error fetching newsletters:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use /api/employees which works for both managers and admins
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        console.error('Failed to fetch employees for departments:', response.status);
        return;
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        const uniqueDepts = [...new Set(data.map(emp => emp.department).filter(Boolean))];
        setDepartments(uniqueDepts);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use /api/employees which works for both managers and admins
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employees`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        console.error('Failed to fetch employees:', response.status);
        return;
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setEmployees(data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  // Section Management
  const addSection = () => {
    const newSection = {
      id: `section-${Date.now()}`,
      columns: [{ id: `col-${Date.now()}`, blocks: [] }],
      columnLayout: 1,
      backgroundColor: '#ffffff',
      padding: '20px'
    };
    setFormData({
      ...formData,
      sections: [...formData.sections, newSection]
    });
  };

  const deleteSection = (sectionIndex) => {
    const updatedSections = formData.sections.filter((_, i) => i !== sectionIndex);
    setFormData({ ...formData, sections: updatedSections });
  };

  const moveSectionUp = (sectionIndex) => {
    if (sectionIndex === 0) return;
    const updatedSections = [...formData.sections];
    [updatedSections[sectionIndex - 1], updatedSections[sectionIndex]] = 
    [updatedSections[sectionIndex], updatedSections[sectionIndex - 1]];
    setFormData({ ...formData, sections: updatedSections });
  };

  const moveSectionDown = (sectionIndex) => {
    if (sectionIndex === formData.sections.length - 1) return;
    const updatedSections = [...formData.sections];
    [updatedSections[sectionIndex], updatedSections[sectionIndex + 1]] = 
    [updatedSections[sectionIndex + 1], updatedSections[sectionIndex]];
    setFormData({ ...formData, sections: updatedSections });
  };

  const updateSectionLayout = (sectionIndex, layout) => {
    const updatedSections = [...formData.sections];
    const currentColumns = updatedSections[sectionIndex].columns.length;
    
    if (layout > currentColumns) {
      // Add columns
      for (let i = currentColumns; i < layout; i++) {
        updatedSections[sectionIndex].columns.push({
          id: `col-${Date.now()}-${i}`,
          blocks: []
        });
      }
    } else if (layout < currentColumns) {
      // Remove columns
      updatedSections[sectionIndex].columns = updatedSections[sectionIndex].columns.slice(0, layout);
    }
    
    updatedSections[sectionIndex].columnLayout = layout;
    setFormData({ ...formData, sections: updatedSections });
  };

  const updateSectionColor = (sectionIndex, color) => {
    const updatedSections = [...formData.sections];
    updatedSections[sectionIndex].backgroundColor = color;
    setFormData({ ...formData, sections: updatedSections });
  };

  // Block Management
  const addBlock = (sectionIndex, columnIndex, blockType) => {
    const updatedSections = [...formData.sections];
    let newBlock;
    
    // Add unique ID to prevent disappearing images
    const blockId = `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    switch (blockType) {
      case 'image':
        newBlock = { id: blockId, type: 'image', url: '', alt: '', link: '', width: '100%' };
        break;
      case 'imageWithText':
        newBlock = { 
          id: blockId, 
          type: 'imageWithText', 
          url: '', 
          alt: '', 
          link: '',
          imagePosition: 'left',
          content: '<p>Your text content here...</p>'
        };
        break;
      case 'heading':
        newBlock = { id: blockId, type: 'heading', level: 'h2', text: 'Heading Text', color: '#000000' };
        break;
      case 'paragraph':
        newBlock = { id: blockId, type: 'paragraph', content: '<p>Enter your text here...</p>' };
        break;
      case 'button':
        newBlock = { id: blockId, type: 'button', text: 'Click Here', link: '', backgroundColor: '#00b48f', textColor: '#ffffff', alignment: 'center' };
        break;
      case 'accordion':
        newBlock = { 
          id: blockId, 
          type: 'accordion', 
          title: 'Accordion Title', 
          content: '<p>Accordion content goes here...</p>', 
          titleColor: '#ffffff',
          titleBgColor: '#00b48f',
          contentColor: '#374151',
          contentBgColor: '#f3f4f6'
        };
        break;
      default:
        return;
    }
    
    updatedSections[sectionIndex].columns[columnIndex].blocks.push(newBlock);
    setFormData({ ...formData, sections: updatedSections });
  };

  const moveBlockUp = (sectionIndex, columnIndex, blockIndex) => {
    if (blockIndex === 0) return;
    const updatedSections = [...formData.sections];
    const blocks = updatedSections[sectionIndex].columns[columnIndex].blocks;
    [blocks[blockIndex - 1], blocks[blockIndex]] = [blocks[blockIndex], blocks[blockIndex - 1]];
    setFormData({ ...formData, sections: updatedSections });
  };

  const moveBlockDown = (sectionIndex, columnIndex, blockIndex) => {
    const updatedSections = [...formData.sections];
    const blocks = updatedSections[sectionIndex].columns[columnIndex].blocks;
    if (blockIndex === blocks.length - 1) return;
    [blocks[blockIndex], blocks[blockIndex + 1]] = [blocks[blockIndex + 1], blocks[blockIndex]];
    setFormData({ ...formData, sections: updatedSections });
  };

  const updateBlock = (sectionIndex, columnIndex, blockIndex, updates) => {
    const updatedSections = [...formData.sections];
    updatedSections[sectionIndex].columns[columnIndex].blocks[blockIndex] = {
      ...updatedSections[sectionIndex].columns[columnIndex].blocks[blockIndex],
      ...updates
    };
    setFormData({ ...formData, sections: updatedSections });
  };

  const deleteBlock = (sectionIndex, columnIndex, blockIndex) => {
    const updatedSections = [...formData.sections];
    updatedSections[sectionIndex].columns[columnIndex].blocks = 
      updatedSections[sectionIndex].columns[columnIndex].blocks.filter((_, i) => i !== blockIndex);
    setFormData({ ...formData, sections: updatedSections });
  };

  // Image Upload
  const handleImageUpload = async (sectionIndex, columnIndex, blockIndex, file) => {
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletters/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      
      if (response.ok) {
        const data = await response.json();
        // Check if it's a data URL (base64) or a path
        const imageUrl = data.url.startsWith('data:') 
          ? data.url  // Already a complete data URL
          : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;  // Legacy path format
        updateBlock(sectionIndex, columnIndex, blockIndex, { url: imageUrl });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    }
  };

  // Save and Publish
  const saveNewsletter = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = currentNewsletter
        ? `${process.env.REACT_APP_BACKEND_URL}/api/newsletters/${currentNewsletter.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/newsletters/create`;
      
      const method = currentNewsletter ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('Newsletter saved successfully!');
        fetchNewsletters();
        resetForm();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error saving newsletter:', error);
      alert('Failed to save newsletter');
    }
    setLoading(false);
  };

  const publishNewsletter = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // First save/create
      let newsletterId = currentNewsletter?.id;
      
      if (!newsletterId) {
        const createResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletters/create`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });
        
        if (!createResponse.ok) {
          const error = await createResponse.json();
          alert(`Error: ${error.detail}`);
          setLoading(false);
          return;
        }
        
        const createData = await createResponse.json();
        newsletterId = createData.newsletter_id;
      } else {
        // Update existing
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletters/${newsletterId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });
      }

      // Publish
      const publishResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletters/${newsletterId}/publish`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (publishResponse.ok) {
        alert('Newsletter published successfully!');
        fetchNewsletters();
        resetForm();
      } else {
        const error = await publishResponse.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error publishing newsletter:', error);
      alert('Failed to publish newsletter');
    }
    setLoading(false);
  };

  const deleteNewsletter = async (newsletterId) => {
    if (!window.confirm('Are you sure you want to delete this newsletter?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletters/${newsletterId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Newsletter deleted successfully');
        fetchNewsletters();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error deleting newsletter:', error);
    }
  };

  const editNewsletter = async (newsletter) => {
    setCurrentNewsletter(newsletter);
    setFormData({
      title: newsletter.title,
      sections: newsletter.sections,
      target_audience: newsletter.target_audience
    });
    setShowBuilder(true);
  };

  const resetForm = () => {
    setCurrentNewsletter(null);
    setFormData({
      title: '',
      sections: [],
      target_audience: {
        type: 'whole_company',
        departments: [],
        employee_ids: []
      }
    });
    setShowBuilder(false);
    setShowPreview(false);
  };

  const draftNewsletters = newsletters.filter(n => n.status === 'draft');
  const publishedNewsletters = newsletters.filter(n => n.status === 'published');

  // Newsletter Preview Component
  const NewsletterPreview = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center">
          <h3 className="text-xl font-bold">Preview: {formData.title}</h3>
          <button
            onClick={() => setShowPreview(false)}
            className="text-gray-600 hover:text-gray-900"
          >
            ✕
          </button>
        </div>
        <div className="p-6">
          {formData.sections.map((section, sIndex) => (
            <div
              key={section.id}
              style={{
                backgroundColor: section.backgroundColor,
                padding: section.padding
              }}
              className="mb-4"
            >
              <div className={`grid gap-4 grid-cols-${section.columnLayout}`}>
                {section.columns.map((column, cIndex) => (
                  <div key={column.id}>
                    {column.blocks.map((block, bIndex) => {
                      if (block.type === 'image') {
                        return (
                          <div key={bIndex} className="mb-4">
                            {block.link ? (
                              <a href={block.link} target="_blank" rel="noopener noreferrer">
                                <img src={block.url} alt={block.alt} style={{ width: block.width }} className="rounded" />
                              </a>
                            ) : (
                              <img src={block.url} alt={block.alt} style={{ width: block.width }} className="rounded" />
                            )}
                          </div>
                        );
                      } else if (block.type === 'heading') {
                        const HeadingTag = block.level;
                        return (
                          <HeadingTag key={bIndex} style={{ color: block.color }} className="mb-4 font-bold">
                            {block.text}
                          </HeadingTag>
                        );
                      } else if (block.type === 'paragraph') {
                        return <div key={bIndex} className="mb-4" dangerouslySetInnerHTML={{ __html: block.content }} />;
                      } else if (block.type === 'button') {
                        return (
                          <div key={bIndex} className={`mb-4 text-${block.alignment}`}>
                            <a
                              href={block.link}
                              style={{
                                backgroundColor: block.backgroundColor,
                                color: block.textColor
                              }}
                              className="inline-block px-6 py-3 rounded-lg font-semibold"
                            >
                              {block.text}
                            </a>
                          </div>
                        );
                      } else if (block.type === 'imageWithText') {
                        return (
                          <div key={bIndex} className={`mb-4 flex gap-4 ${block.imagePosition === 'right' ? 'flex-row-reverse' : 'flex-row'}`}>
                            <div className="w-1/3 flex-shrink-0">
                              {block.link ? (
                                <a href={block.link} target="_blank" rel="noopener noreferrer">
                                  <img src={block.url} alt={block.alt} className="w-full rounded" />
                                </a>
                              ) : (
                                <img src={block.url} alt={block.alt} className="w-full rounded" />
                              )}
                            </div>
                            <div className="flex-1 prose" dangerouslySetInnerHTML={{ __html: block.content }} />
                          </div>
                        );
                      } else if (block.type === 'accordion') {
                        return (
                          <details key={bIndex} className="mb-4 rounded-lg overflow-hidden">
                            <summary 
                              style={{ backgroundColor: block.titleBgColor, color: block.titleColor }}
                              className="px-4 py-3 cursor-pointer font-semibold"
                            >
                              {block.title}
                            </summary>
                            <div 
                              style={{ backgroundColor: block.contentBgColor, color: block.contentColor }}
                              className="px-4 py-3 prose"
                              dangerouslySetInnerHTML={{ __html: block.content }}
                            />
                          </details>
                        );
                      }
                      return null;
                    })}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (showBuilder) {
    return (
      <div className="space-y-6">
        {showPreview && <NewsletterPreview />}
        
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={resetForm}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Newsletters
            </button>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowPreview(true)}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </button>
            </div>
          </div>

          {/* Title */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Newsletter Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter newsletter title"
            />
          </div>

          {/* Summary */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Summary</label>
            <textarea
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Brief summary of the newsletter (shown in preview)"
              rows={3}
            />
          </div>

          {/* Target Audience */}
          <div className="mb-6 p-4 border rounded-lg bg-gray-50">
            <label className="block text-sm font-medium text-gray-700 mb-2">Target Audience</label>
            <select
              value={formData.target_audience.type}
              onChange={(e) => setFormData({
                ...formData,
                target_audience: {
                  ...formData.target_audience,
                  type: e.target.value,
                  departments: [],
                  employee_ids: []
                }
              })}
              className="w-full px-4 py-2 border rounded-lg mb-2"
            >
              <option value="whole_company">Whole Company</option>
              <option value="by_department">By Department</option>
              <option value="individual">Individual Employees</option>
            </select>

            {formData.target_audience.type === 'by_department' && (
              <div className="mt-2">
                <label className="block text-sm text-gray-600 mb-1">Select Departments</label>
                <div className="space-y-1">
                  {departments.map(dept => (
                    <label key={dept} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.target_audience.departments.includes(dept)}
                        onChange={(e) => {
                          const depts = e.target.checked
                            ? [...formData.target_audience.departments, dept]
                            : formData.target_audience.departments.filter(d => d !== dept);
                          setFormData({
                            ...formData,
                            target_audience: { ...formData.target_audience, departments: depts }
                          });
                        }}
                        className="mr-2"
                      />
                      {dept}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {formData.target_audience.type === 'individual' && (
              <div className="mt-2">
                <label className="block text-sm text-gray-600 mb-1">Select Employees</label>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {employees.map(emp => (
                    <label key={emp.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.target_audience.employee_ids.includes(emp.id)}
                        onChange={(e) => {
                          const ids = e.target.checked
                            ? [...formData.target_audience.employee_ids, emp.id]
                            : formData.target_audience.employee_ids.filter(id => id !== emp.id);
                          setFormData({
                            ...formData,
                            target_audience: { ...formData.target_audience, employee_ids: ids }
                          });
                        }}
                        className="mr-2"
                      />
                      {emp.first_name} {emp.last_name}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sections */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Content Sections</h3>
              <button
                onClick={addSection}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Section
              </button>
            </div>

            <div className="space-y-6">
              {formData.sections.map((section, sIndex) => (
                <div key={section.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  {/* Section Header */}
                  <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border-b border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => toggleSectionCollapse(sIndex)}
                          className="p-1 rounded hover:bg-white transition-colors"
                          title={collapsedSections.has(sIndex) ? "Expand section" : "Collapse section"}
                        >
                          {collapsedSections.has(sIndex) ? (
                            <ChevronRight className="w-5 h-5 text-gray-600" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-600" />
                          )}
                        </button>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => moveSectionUp(sIndex)}
                            disabled={sIndex === 0}
                            className="p-1 rounded hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title="Move section up"
                          >
                            <ChevronUp className="w-5 h-5 text-gray-600" />
                          </button>
                          <button
                            onClick={() => moveSectionDown(sIndex)}
                            disabled={sIndex === formData.sections.length - 1}
                            className="p-1 rounded hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title="Move section down"
                          >
                            <ChevronDown className="w-5 h-5 text-gray-600" />
                          </button>
                        </div>
                        <GripVertical className="w-5 h-5 text-gray-400" />
                        <span className="font-semibold text-gray-900">Section {sIndex + 1}</span>
                        {collapsedSections.has(sIndex) && (
                          <span className="text-xs text-gray-500 italic">
                            ({section.columns.reduce((sum, col) => sum + col.blocks.length, 0)} blocks)
                          </span>
                        )}
                      </div>
                      
                      <button
                        onClick={() => deleteSection(sIndex)}
                        className="p-2 rounded-lg hover:bg-red-50 text-red-600 transition-colors"
                        title="Delete section"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Section Settings - Hidden when collapsed */}
                  {!collapsedSections.has(sIndex) && (
                  <div className="p-4 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center gap-4 flex-wrap">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Layout</label>
                        <div className="flex gap-2">
                          {[1, 2, 3, 4].map((num) => (
                            <button
                              key={num}
                              onClick={() => updateSectionLayout(sIndex, num)}
                              className={`px-3 py-1.5 text-sm rounded-lg border-2 transition-all ${
                                section.columnLayout === num
                                  ? 'border-teal-500 bg-teal-50 text-teal-700 font-semibold'
                                  : 'border-gray-200 hover:border-gray-300 text-gray-600'
                              }`}
                            >
                              {num} Col
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Background</label>
                        <div className="flex gap-2">
                          {BRAND_COLORS.map((color) => (
                            <button
                              key={color.value}
                              onClick={() => updateSectionColor(sIndex, color.value)}
                              className={`w-8 h-8 rounded-lg border-2 transition-all ${
                                section.backgroundColor === color.value
                                  ? 'border-teal-500 scale-110'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              style={{ backgroundColor: color.value }}
                              title={color.name}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Columns - Hidden when collapsed */}
                  {!collapsedSections.has(sIndex) && (
                  <div className={`grid gap-4 p-4 ${
                    section.columnLayout === 1 ? 'grid-cols-1' :
                    section.columnLayout === 2 ? 'grid-cols-2' :
                    section.columnLayout === 3 ? 'grid-cols-3' :
                    'grid-cols-4'
                  }`}>
                    {section.columns.map((column, cIndex) => (
                      <div key={column.id} className="border border-dashed border-gray-300 rounded p-3 min-h-[100px]">
                        <div className="text-xs font-medium text-gray-500 mb-2">Column {cIndex + 1}</div>
                        
                        {/* Add Block Buttons */}
                        <div className="flex flex-wrap gap-1 mb-3">
                          <button
                            onClick={() => addBlock(sIndex, cIndex, 'image')}
                            className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                            title="Add Image"
                          >
                            <ImageIcon className="w-3 h-3 inline mr-1" />Img
                          </button>
                          <button
                            onClick={() => addBlock(sIndex, cIndex, 'imageWithText')}
                            className="px-2 py-1 text-xs bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200"
                            title="Add Image with Text"
                          >
                            <FileText className="w-3 h-3 inline mr-1" />Img+T
                          </button>
                          <button
                            onClick={() => addBlock(sIndex, cIndex, 'heading')}
                            className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                            title="Add Heading"
                          >
                            <Type className="w-3 h-3 inline mr-1" />H
                          </button>
                          <button
                            onClick={() => addBlock(sIndex, cIndex, 'paragraph')}
                            className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                            title="Add Rich Text"
                          >
                            <AlignLeft className="w-3 h-3 inline mr-1" />Text
                          </button>
                          <button
                            onClick={() => addBlock(sIndex, cIndex, 'button')}
                            className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                            title="Add Button"
                          >
                            <Square className="w-3 h-3 inline mr-1" />Btn
                          </button>
                          <button
                            onClick={() => addBlock(sIndex, cIndex, 'accordion')}
                            className="px-2 py-1 text-xs bg-teal-100 text-teal-700 rounded hover:bg-teal-200"
                            title="Add Accordion"
                          >
                            <List className="w-3 h-3 inline mr-1" />Acc
                          </button>
                        </div>

                        {/* Blocks */}
                        <div className="space-y-3">
                          {column.blocks.map((block, bIndex) => {
                            const blockKey = block.id || `${sIndex}-${cIndex}-${bIndex}`;
                            return (
                            <div key={blockKey} className="border-2 border-gray-200 rounded-lg p-3 bg-white hover:border-teal-300 transition-colors">
                              <div className="flex justify-between items-center mb-2">
                                <div className="flex items-center gap-2">
                                  <div className="flex gap-0.5">
                                    <button
                                      onClick={() => moveBlockUp(sIndex, cIndex, bIndex)}
                                      disabled={bIndex === 0}
                                      className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30"
                                      title="Move up"
                                    >
                                      <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
                                    </button>
                                    <button
                                      onClick={() => moveBlockDown(sIndex, cIndex, bIndex)}
                                      disabled={bIndex === column.blocks.length - 1}
                                      className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30"
                                      title="Move down"
                                    >
                                      <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                                    </button>
                                  </div>
                                  <span className="text-xs font-medium text-gray-600 capitalize">{block.type}</span>
                                </div>
                                <button
                                  onClick={() => deleteBlock(sIndex, cIndex, bIndex)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>

                              {/* Block-specific inputs */}
                              {block.type === 'image' && (
                                <div className="space-y-2">
                                  {block.url ? (
                                    <div className="relative">
                                      <img src={block.url} alt={block.alt} className="w-full rounded" />
                                      <button
                                        onClick={() => updateBlock(sIndex, cIndex, bIndex, { url: '' })}
                                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ) : (
                                    <label className="flex items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-teal-500 transition-colors">
                                      <div className="text-center">
                                        <Upload className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                                        <span className="text-xs text-gray-500">Upload Image</span>
                                      </div>
                                      <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => handleImageUpload(sIndex, cIndex, bIndex, e.target.files[0])}
                                      />
                                    </label>
                                  )}
                                  <input
                                    type="text"
                                    placeholder="Alt text"
                                    value={block.alt}
                                    onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { alt: e.target.value })}
                                    className="w-full px-2 py-1 text-xs border rounded"
                                  />
                                  <input
                                    type="text"
                                    placeholder="Link URL (optional)"
                                    value={block.link}
                                    onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { link: e.target.value })}
                                    className="w-full px-2 py-1 text-xs border rounded"
                                  />
                                </div>
                              )}

                              {block.type === 'heading' && (
                                <div className="space-y-2">
                                  <select
                                    value={block.level}
                                    onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { level: e.target.value })}
                                    className="w-full px-2 py-1 text-xs border rounded"
                                  >
                                    <option value="h1">H1</option>
                                    <option value="h2">H2</option>
                                    <option value="h3">H3</option>
                                  </select>
                                  <input
                                    type="text"
                                    value={block.text}
                                    onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { text: e.target.value })}
                                    className="w-full px-2 py-1 text-xs border rounded"
                                    placeholder="Heading text"
                                  />
                                  <input
                                    type="color"
                                    value={block.color}
                                    onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { color: e.target.value })}
                                    className="w-full h-8"
                                  />
                                </div>
                              )}

                              {block.type === 'paragraph' && (
                                <div className="space-y-2">
                                  <RichTextEditor
                                    value={block.content}
                                    onChange={(value) => updateBlock(sIndex, cIndex, bIndex, { content: value })}
                                    placeholder="Enter your content..."
                                  />
                                </div>
                              )}

                              {block.type === 'button' && (
                                <div className="space-y-2">
                                  <input
                                    type="text"
                                    placeholder="Button text"
                                    value={block.text}
                                    onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { text: e.target.value })}
                                    className="w-full px-2 py-1 text-xs border rounded"
                                  />
                                  <input
                                    type="text"
                                    placeholder="Link URL"
                                    value={block.link}
                                    onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { link: e.target.value })}
                                    className="w-full px-2 py-1 text-xs border rounded"
                                  />
                                  <div className="grid grid-cols-2 gap-2">
                                    <div>
                                      <label className="text-xs text-gray-600">Background</label>
                                      <input
                                        type="color"
                                        value={block.backgroundColor}
                                        onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { backgroundColor: e.target.value })}
                                        className="w-full h-6"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-gray-600">Text Color</label>
                                      <input
                                        type="color"
                                        value={block.textColor}
                                        onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { textColor: e.target.value })}
                                        className="w-full h-6"
                                      />
                                    </div>
                                  </div>
                                  <div className="flex gap-1 flex-wrap items-center">
                                    <span className="text-xs text-gray-500">Brand colors:</span>
                                    {BRAND_COLORS.map((color) => (
                                      <button
                                        key={color.value}
                                        onClick={() => updateBlock(sIndex, cIndex, bIndex, { backgroundColor: color.value })}
                                        className="w-6 h-6 rounded border-2 border-gray-300 hover:border-teal-500"
                                        style={{ backgroundColor: color.value }}
                                        title={`Set to ${color.name}`}
                                      />
                                    ))}
                                  </div>
                                  <select
                                    value={block.alignment}
                                    onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { alignment: e.target.value })}
                                    className="w-full px-2 py-1 text-xs border rounded"
                                  >
                                    <option value="left">Left</option>
                                    <option value="center">Center</option>
                                    <option value="right">Right</option>
                                  </select>
                                </div>
                              )}

                              {block.type === 'imageWithText' && (
                                <div className="space-y-2">
                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">Image Position</label>
                                    <select
                                      value={block.imagePosition || 'left'}
                                      onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { imagePosition: e.target.value })}
                                      className="w-full px-2 py-1 text-xs border rounded"
                                    >
                                      <option value="left">Image on Left</option>
                                      <option value="right">Image on Right</option>
                                    </select>
                                  </div>
                                  {block.url ? (
                                    <div className="relative">
                                      <img src={block.url} alt={block.alt} className="w-full rounded" />
                                      <button
                                        onClick={() => updateBlock(sIndex, cIndex, bIndex, { url: '' })}
                                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ) : (
                                    <label className="flex items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-teal-500 transition-colors">
                                      <div className="text-center">
                                        <Upload className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                                        <span className="text-xs text-gray-500">Upload Image</span>
                                      </div>
                                      <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => handleImageUpload(sIndex, cIndex, bIndex, e.target.files[0])}
                                      />
                                    </label>
                                  )}
                                  <input
                                    type="text"
                                    placeholder="Alt text"
                                    value={block.alt || ''}
                                    onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { alt: e.target.value })}
                                    className="w-full px-2 py-1 text-xs border rounded"
                                  />
                                  <input
                                    type="text"
                                    placeholder="Link URL (optional)"
                                    value={block.link || ''}
                                    onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { link: e.target.value })}
                                    className="w-full px-2 py-1 text-xs border rounded"
                                  />
                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">Text Content</label>
                                    <RichTextEditor
                                      value={block.content || ''}
                                      onChange={(value) => updateBlock(sIndex, cIndex, bIndex, { content: value })}
                                      placeholder="Enter text content..."
                                    />
                                  </div>
                                </div>
                              )}

                              {block.type === 'accordion' && (
                                <div className="space-y-2">
                                  <input
                                    type="text"
                                    placeholder="Accordion title"
                                    value={block.title || ''}
                                    onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { title: e.target.value })}
                                    className="w-full px-2 py-1 text-sm font-medium border rounded"
                                  />
                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">Content</label>
                                    <RichTextEditor
                                      value={block.content || ''}
                                      onChange={(value) => updateBlock(sIndex, cIndex, bIndex, { content: value })}
                                      placeholder="Enter accordion content..."
                                    />
                                  </div>
                                  <div className="grid grid-cols-2 gap-2">
                                    <div>
                                      <label className="text-xs text-gray-600">Title Text</label>
                                      <input
                                        type="color"
                                        value={block.titleColor || '#ffffff'}
                                        onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { titleColor: e.target.value })}
                                        className="w-full h-6"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-gray-600">Title BG</label>
                                      <input
                                        type="color"
                                        value={block.titleBgColor || '#00b48f'}
                                        onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { titleBgColor: e.target.value })}
                                        className="w-full h-6"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-gray-600">Content Text</label>
                                      <input
                                        type="color"
                                        value={block.contentColor || '#374151'}
                                        onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { contentColor: e.target.value })}
                                        className="w-full h-6"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-gray-600">Content BG</label>
                                      <input
                                        type="color"
                                        value={block.contentBgColor || '#f3f4f6'}
                                        onChange={(e) => updateBlock(sIndex, cIndex, bIndex, { contentBgColor: e.target.value })}
                                        className="w-full h-6"
                                      />
                                    </div>
                                  </div>
                                  <div className="flex gap-1 flex-wrap items-center">
                                    <span className="text-xs text-gray-500">Quick title BG:</span>
                                    {BRAND_COLORS.map((color) => (
                                      <button
                                        key={color.value}
                                        onClick={() => updateBlock(sIndex, cIndex, bIndex, { titleBgColor: color.value })}
                                        className="w-6 h-6 rounded border-2 border-gray-300 hover:border-teal-500"
                                        style={{ backgroundColor: color.value }}
                                        title={`Set to ${color.name}`}
                                      />
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              onClick={saveNewsletter}
              disabled={loading}
              className="flex items-center px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Draft
            </button>
            <button
              onClick={publishNewsletter}
              disabled={loading}
              className="flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <Send className="w-4 h-4 mr-2" />
              Publish
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Newsletter List View
  return (
    <div className="space-y-6 overflow-x-hidden">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col gap-4">
          <Button onClick={() => navigate('/admin/dashboard')} variant="ghost" size="sm" className="self-start">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Newsletters</h1>
            <p className="text-sm text-gray-500 mt-1">Create and manage company newsletters</p>
          </div>
          <button
            onClick={() => setShowBuilder(true)}
            className="flex items-center justify-center px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 w-full sm:w-auto"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Newsletter
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <div className="flex space-x-8">
          <button
            onClick={() => setActiveTab('drafts')}
            className={`pb-3 px-1 ${
              activeTab === 'drafts'
                ? 'border-b-2 border-blue-600 text-blue-600 font-semibold'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Drafts ({draftNewsletters.length})
          </button>
          <button
            onClick={() => setActiveTab('published')}
            className={`pb-3 px-1 ${
              activeTab === 'published'
                ? 'border-b-2 border-blue-600 text-blue-600 font-semibold'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Published ({publishedNewsletters.length})
          </button>
        </div>
      </div>

      {/* Newsletter Cards */}
      <div className="grid gap-6">
        {(activeTab === 'drafts' ? draftNewsletters : publishedNewsletters).map(newsletter => (
          <div key={newsletter.id} className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{newsletter.title}</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  {newsletter.summary && <p className="text-gray-700">{newsletter.summary}</p>}
                  <p>Target: {newsletter.target_audience.type.replace('_', ' ')}</p>
                  {newsletter.published_at && (
                    <p>Published: {new Date(newsletter.published_at).toLocaleDateString()}</p>
                  )}
                </div>
              </div>
              <div className="flex space-x-2">
                {newsletter.status === 'draft' && (
                  <>
                    <button
                      onClick={() => editNewsletter(newsletter)}
                      className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteNewsletter(newsletter.id)}
                      className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      Delete
                    </button>
                  </>
                )}
                {newsletter.status === 'published' && (
                  <button
                    onClick={() => editNewsletter(newsletter)}
                    className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    View
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {(activeTab === 'drafts' ? draftNewsletters : publishedNewsletters).length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg">
            <p className="text-gray-500">
              No {activeTab} newsletters yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsletterBuilder;
